import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { RefreshCw, Settings, Moon, Sun, Calendar, Coffee, Search } from "react-feather";
import {
  useGetGuardRequirementsForOrderQuery,
  useGetGuardRosterCandidatesQuery,
  useConfirmGuardRosterSelectionMutation,
} from "../../guardingApi";
import { useCanEditDeployment } from "../../../sales/useSalesPermissions";
import RecommendedGuardsTable from "./RecommendedGuardsTable";
import SelectedGuardsPanel from "./SelectedGuardsPanel";
import DeploymentContextCards from "../DeploymentContextCards";
import { linesForRequest } from "../../pendingRequest";

const DEFAULT_RULES = { minRestHours: 12, nightToDayAllowed: false, maxConsecutiveDays: 6, weeklyRestDays: 1 };

const BATCH_SIZE = 10;

// Everything about the deployment itself - customer, area, guards required, shift, assignment
// type, and the starting date - comes from the real customer request captured at Guard
// Assignment creation time (via Guard Assignments / the requirement line), never from a filter
// on this screen. "There is no one who can edit these details other than when a customer
// requests so and the one who creates the deployment" - direct instruction. Day and Night can
// carry different guard counts for the same customer, which is why a deployment can have more
// than one requirement line (see the line selector below) - each line is its own real request,
// not a variant to merge away. Only the rule thresholds (rest hours, night->day, consecutive
// days, weekly rest) are legitimately adjustable here.
//
// `order` is a Pending Deployments row, i.e. one deployment REQUEST (Batch161) - so only
// the lines of that request (same start date + service) are offered here, not every line
// on the order.
const AiGuardRosterTab = ({ order }) => {
  const canEdit = useCanEditDeployment();
  const { data: orderLines = [], isLoading: linesLoading } = useGetGuardRequirementsForOrderQuery(order.recordGuid);
  const lines = useMemo(() => linesForRequest(orderLines, order), [orderLines, order]);

  const [selectedLineId, setSelectedLineId] = useState(null);
  const activeLine = useMemo(
    () => lines.find((l) => l.lineId === selectedLineId) || lines[0],
    [lines, selectedLineId]
  );
  // The requested date is the request's own start date - a pending row is the lines sharing
  // one Start Date (Batch161), and the order form no longer asks for a separate deployment
  // date (2026-09-11). The line's StartDate is only a fallback for a row with no date.
  const usingFallbackDate = !order.requestedDeploymentDate;
  const proposedStartDate = activeLine
    ? (order.requestedDeploymentDate || activeLine.startDate).slice(0, 10)
    : null;

  const [rules, setRules] = useState(DEFAULT_RULES);
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [selectedGuids, setSelectedGuids] = useState(new Set());
  const [selectedRecords, setSelectedRecords] = useState(new Map());
  // pageIndex picks which 10-row window is shown; fetchedCount is only how much has been
  // pulled from the server so far (grows ahead of pageIndex so the next page always has data
  // ready). "Show 10 More" REPLACES the visible 10 with the next 10 - it never grows the
  // on-screen list, per direct instruction.
  const [pageIndex, setPageIndex] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [fetchedCount, setFetchedCount] = useState(BATCH_SIZE);

  // A rule change or switching requirement lines starts back at page 1 - only Show 10 More
  // should move to a later page of the same result set.
  const filterKey = JSON.stringify({ lineId: activeLine?.lineId, rules });
  useEffect(() => {
    setPageIndex(0);
    setFetchedCount(BATCH_SIZE);
  }, [filterKey]);

  // Day and Night are separate real requests with separate counts - a selection made while
  // rostering one must never leak into the other, so switching lines clears it.
  useEffect(() => {
    setSelectedGuids(new Set());
    setSelectedRecords(new Map());
  }, [activeLine?.lineId]);

  const remainingSlots = activeLine ? Math.max(0, activeLine.qty - activeLine.deployedQty) : 0;

  // Debounced so a round trip does not fire on every keystroke - the roster
  // query scores the whole branch.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const rosterParams = activeLine
    ? {
        orderRecordGuid: order.recordGuid,
        lineId: activeLine.lineId,
        proposedStartDate,
        minRestHours: rules.minRestHours,
        nightToDayAllowed: rules.nightToDayAllowed,
        maxConsecutiveDays: rules.maxConsecutiveDays,
        weeklyRestDays: rules.weeklyRestDays,
        maxResults: Math.max(50, fetchedCount),
        // Filtered in SQL, before scoring and the page cap (Batch148). Doing it
        // over `allCandidates` here would look right and silently fail: the
        // scored list is capped, so the person being searched for is usually
        // not in what came back.
        search: search.trim() || undefined,
      }
    : undefined;

  const { data: roster, isFetching: rosterFetching, refetch } = useGetGuardRosterCandidatesQuery(rosterParams, {
    skip: !activeLine,
  });
  const [confirmSelection, { isLoading: isConfirming }] = useConfirmGuardRosterSelectionMutation();

  const allCandidates = roster?.candidates ?? [];
  const totalCandidateCount = roster?.totalCandidateCount ?? 0;
  const pageStart = pageIndex * BATCH_SIZE;
  const candidates = allCandidates.slice(pageStart, pageStart + BATCH_SIZE);
  const hasMore = pageStart + BATCH_SIZE < totalCandidateCount;

  const handleRefreshRecommendations = () => {
    setPageIndex(0);
    setFetchedCount(BATCH_SIZE);
    refetch();
  };
  const handleShowMore = () => {
    setPageIndex((p) => p + 1);
    setFetchedCount((c) => Math.max(c, pageStart + 2 * BATCH_SIZE));
  };

  const toggleSelect = (candidate) => {
    const isSelected = selectedGuids.has(candidate.employeeRecordGuid);
    if (!isSelected && selectedGuids.size >= remainingSlots) {
      toast.error(`Only ${remainingSlots} more guard${remainingSlots === 1 ? "" : "s"} needed for this ${activeLine.shift === "NIGHT" ? "Night" : "Day"} line - deselect one first.`);
      return;
    }
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(candidate.employeeRecordGuid)) next.delete(candidate.employeeRecordGuid);
      else next.add(candidate.employeeRecordGuid);
      return next;
    });
    setSelectedRecords((prev) => {
      const next = new Map(prev);
      if (next.has(candidate.employeeRecordGuid)) next.delete(candidate.employeeRecordGuid);
      else next.set(candidate.employeeRecordGuid, candidate);
      return next;
    });
  };

  const removeSelected = (candidate) => toggleSelect(candidate);

  const handleConfirm = async () => {
    if (!activeLine) return;
    try {
      await confirmSelection({
        orderRecordGuid: order.recordGuid,
        lineId: activeLine.lineId,
        selectedEmployeeRecordGuids: Array.from(selectedGuids),
      }).unwrap();
      toast.success("Guard(s) assigned");
      setSelectedGuids(new Set());
      setSelectedRecords(new Map());
    } catch (error) {
      toast.error(error?.message || "Could not confirm the selection");
    }
  };

  if (linesLoading) return <div className="text-muted">Loading...</div>;

  if (lines.length === 0) {
    return <div className="text-muted">No guard requirement lines are recorded on this order.</div>;
  }

  return (
    <div>
      <DeploymentContextCards
        order={order}
        guardsCount={activeLine.qty}
        deploymentType={activeLine.deploymentType}
        shift={activeLine.shift}
        requestedDate={order.requestedDeploymentDate || activeLine.startDate}
        requestedDateIsFallback={usingFallbackDate}
      />

      {lines.length > 1 && (
        <div className="card mb-2 border-primary">
          <div className="card-body py-2 px-3">
            <div className="text-muted small mb-2">This customer requested multiple guard lines - roster each shift separately:</div>
            <div className="btn-group" role="group">
              {lines.map((l) => {
                const isActive = l.lineId === activeLine.lineId;
                return (
                  <button
                    key={l.lineId}
                    type="button"
                    className={`btn ${isActive ? "btn-primary" : "btn-outline-primary"} fw-semibold px-4`}
                    onClick={() => setSelectedLineId(l.lineId)}
                  >
                    {l.shift === "NIGHT" ? "🌙 Night" : "☀ Day"} Shift &middot; {l.deployedQty}/{l.qty} assigned
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="card mb-2">
        <div className="card-body p-2 px-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => setRulesExpanded((v) => !v)}>
              <Settings size={13} /> Rules Applied
            </button>
            <span className="badge badge-linelight d-inline-flex align-items-center gap-1"><Moon size={11} /> Min Rest: {rules.minRestHours}h</span>
            <span className="badge badge-linelight d-inline-flex align-items-center gap-1"><Sun size={11} /> Night → Day: {rules.nightToDayAllowed ? "Allowed" : "Not Allowed"}</span>
            <span className="badge badge-linelight d-inline-flex align-items-center gap-1"><Calendar size={11} /> Max Consecutive Days: {rules.maxConsecutiveDays}</span>
            <span className="badge badge-linelight d-inline-flex align-items-center gap-1"><Coffee size={11} /> Weekly Rest: {rules.weeklyRestDays} Day in 7</span>
            <div className="ms-auto d-flex gap-2">
              {hasMore && (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleShowMore} disabled={rosterFetching}>
                  Show 10 More
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
                onClick={handleRefreshRecommendations}
                disabled={rosterFetching}
              >
                <RefreshCw size={14} /> Refresh Recommendations
              </button>
            </div>
          </div>

          {rulesExpanded && (
            <div className="row g-2 border-top pt-2 mt-2">
              <div className="col-md-3">
                <label className="form-label small mb-1">Minimum Rest Between Shifts (hours)</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={rules.minRestHours}
                  onChange={(e) => setRules((r) => ({ ...r, minRestHours: Number(e.target.value) }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1 d-block">Night → Day</label>
                <div className="form-check form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={rules.nightToDayAllowed}
                    onChange={(e) => setRules((r) => ({ ...r, nightToDayAllowed: e.target.checked }))}
                  />
                  <span className="small text-muted">{rules.nightToDayAllowed ? "Allowed" : "Not Allowed"}</span>
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Max Consecutive Working Days</label>
                <input
                  type="number"
                  min={1}
                  className="form-control form-control-sm"
                  value={rules.maxConsecutiveDays}
                  onChange={(e) => setRules((r) => ({ ...r, maxConsecutiveDays: Number(e.target.value) }))}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small mb-1">Weekly Rest (days in 7)</label>
                <input
                  type="number"
                  min={0}
                  max={7}
                  className="form-control form-control-sm"
                  value={rules.weeklyRestDays}
                  onChange={(e) => setRules((r) => ({ ...r, weeklyRestDays: Number(e.target.value) }))}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body p-2 px-3">
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
            <h6 className="mb-0">Recommended Guards</h6>
            <span className="text-muted small">
              {rosterFetching
                ? "Loading..."
                : roster && (
                    <>
                      <span className="text-success">{roster.recommendedCount} recommended</span> ·{" "}
                      <span className="text-warning">{roster.possibleReviewCount} review</span> ·{" "}
                      <span className="text-danger">{roster.notEligibleCount} not eligible</span>{" "}
                      (of {totalCandidateCount} total)
                    </>
                  )}
            </span>
          </div>

          {/* Searching a specific guard by name or code. This narrows what the
              SERVER scores, not what is already on screen - the scored list is
              capped, so a browser-side filter would miss most people. */}
          <div className="input-group input-group-sm mb-2" style={{ maxWidth: 320 }}>
            <span className="input-group-text"><Search size={14} /></span>
            <input
              type="text"
              className="form-control"
              placeholder="Find a guard by name or employee code"
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setPageIndex(0); }}
            />
            {searchInput && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => { setSearchInput(""); setPageIndex(0); }}>
                Clear
              </button>
            )}
          </div>
          {search && !rosterFetching && totalCandidateCount === 0 && (
            <p className="text-muted small mb-2">
              No guard matches "{search}" in this branch. Only active OPERATION-department guards appear here.
            </p>
          )}

          <RecommendedGuardsTable
            candidates={candidates}
            selectedGuids={selectedGuids}
            onToggleSelect={toggleSelect}
            canEdit={canEdit}
            atSelectionCap={selectedGuids.size >= remainingSlots}
          />
        </div>
      </div>

      {canEdit && (
        <SelectedGuardsPanel
          selected={Array.from(selectedRecords.values())}
          requiredQty={remainingSlots}
          onRemove={removeSelected}
          onConfirm={handleConfirm}
          isConfirming={isConfirming}
        />
      )}
    </div>
  );
};

export default AiGuardRosterTab;
